(() => {
  const RANGE_SETS = {
    kg: {
      'under-50': { min: 0, max: 50 },
      '50-100': { min: 50, max: 100 },
      '100-200': { min: 100, max: 200 },
      '200-500': { min: 200, max: 500 },
      '500-plus': { min: 500, max: Infinity },
    },
    cm: {
      'under-40': { min: 0, max: 40 },
      '40-60': { min: 40, max: 60 },
      '60-80': { min: 60, max: 80 },
      '80-100': { min: 80, max: 100 },
      '100-plus': { min: 100, max: Infinity },
    },
    load_kg: {
      'under-70': { min: 0, max: 70 },
      '70-100': { min: 70, max: 100 },
      '100-150': { min: 100, max: 150 },
      '150-plus': { min: 150, max: Infinity },
    },
  };

  const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
  ]);

  function formatThousands(value) {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n)) return String(value ?? '');
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function parseNum(str) {
    if (str == null || str === '') return NaN;
    const n = parseFloat(String(str).replace(/\s/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  }

  function parseLocaleNumber(value) {
    if (value === null || value === undefined) return NaN;
    let normalized = String(value).trim();
    if (!normalized) return NaN;

    normalized = normalized.replace(/\s|'/g, '');

    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');

    const looksLikeThousandsGrouping = (str, separator) => {
      const parts = str.split(separator);
      if (parts.length < 2) return false;
      if (!/^\d{1,3}$/.test(parts[0])) return false;
      for (let i = 1; i < parts.length; i += 1) {
        if (!/^\d{3}$/.test(parts[i])) return false;
      }
      return true;
    };

    if (lastComma > -1 && lastDot > -1) {
      if (lastComma > lastDot) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
    } else if (lastComma > -1) {
      if (looksLikeThousandsGrouping(normalized, ',')) {
        normalized = normalized.replace(/,/g, '');
      } else {
        normalized = normalized.replace(',', '.');
      }
    } else if (lastDot > -1) {
      if (looksLikeThousandsGrouping(normalized, '.')) {
        normalized = normalized.replace(/\./g, '');
      }
    }

    return parseFloat(normalized);
  }

  function parseMoneyInput(value, currency) {
    const n = parseLocaleNumber(value);
    if (!Number.isFinite(n)) return null;
    if (ZERO_DECIMAL_CURRENCIES.has(currency)) return Math.round(n);
    return Math.round(n * 100);
  }

  function parseStoredPrice(value) {
    if (value == null || value === '') return 0;
    const n = parseLocaleNumber(String(value));
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  function readPriceFromCard(item) {
    const priceEl = item.querySelector('.price-item--sale, .price-item--regular');
    if (!priceEl) return 0;
    const digits = priceEl.textContent.replace(/[^\d.,]/g, '');
    const parsed = parseLocaleNumber(digits);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
  }

  function getProductPrices(root, item) {
    let min = parseStoredPrice(root.getAttribute('data-mf-price'));
    let max = parseStoredPrice(root.getAttribute('data-mf-price-max'));
    if (max < min) max = min;

    if (min <= 0 && item) {
      const fromCard = readPriceFromCard(item);
      if (fromCard > 0) {
        min = fromCard;
        max = Math.max(max, min);
      }
    }

    return { min, max: Math.max(min, max) };
  }

  function matchesPrice(priceMin, priceMax, filters) {
    if (priceMin <= 0 && priceMax <= 0) return false;
    const lo = Math.min(priceMin, priceMax);
    const hi = Math.max(priceMin, priceMax);
    if (filters.priceMin != null && hi < filters.priceMin) return false;
    if (filters.priceMax != null && lo > filters.priceMax) return false;
    return true;
  }

  function getFilterRoot(item) {
    return item.querySelector('[data-mf-filter-root]') || item;
  }

  function hasFilterField(root, key) {
    const attr = root.getAttribute(`data-mf-${key}-num`);
    return attr != null && String(attr).trim() !== '';
  }

  function hasListField(root, key) {
    const attr = root.getAttribute(`data-mf-${key}`);
    return attr != null && String(attr).trim() !== '';
  }

  function getFieldNum(root, item, key) {
    const numAttr = root.getAttribute(`data-mf-${key}-num`);
    if (numAttr != null && String(numAttr).trim() !== '') {
      const n = parseNum(numAttr);
      if (!Number.isNaN(n)) return n;
    }

    if (key === 'weight') {
      const weightEl = item?.querySelector('.product-weight__value');
      if (weightEl) {
        const match = weightEl.textContent.match(/[\d]+(?:[.,][\d]+)?/);
        if (match) {
          const n = parseNum(match[0]);
          if (!Number.isNaN(n) && n > 0) return n;
        }
      }
    }

    return NaN;
  }

  function sortValueLists(root) {
    root.querySelectorAll('.mf-filter-values').forEach((ul) => {
      const sortMode = ul.dataset.sort || 'numeric';
      if (sortMode === 'none') return;
      const items = Array.from(ul.querySelectorAll(':scope > .facets__item'));

      items.sort((a, b) => {
        const aVal = a.querySelector('input')?.value || '';
        const bVal = b.querySelector('input')?.value || '';
        if (sortMode === 'alpha') return aVal.localeCompare(bVal, 'is');
        const aNum = parseNum(aVal);
        const bNum = parseNum(bVal);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
        return aVal.localeCompare(bVal, 'is');
      });

      items.forEach((li) => ul.appendChild(li));
    });
  }

  function matchesRange(num, selections) {
    if (Number.isNaN(num)) return false;
    return selections.some(({ id, set }) => {
      const range = RANGE_SETS[set]?.[id];
      if (!range) return false;
      if (range.max === Infinity) return num >= range.min;
      return num >= range.min && num < range.max;
    });
  }

  class CollectionMetafieldFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.currency = this.dataset.currency || 'ISK';
      this.form = this.querySelector('form');
      this._onRefresh = () => this.apply();

      sortValueLists(this);

      if (this.form) {
        this.querySelector('[data-mf-clear]')?.addEventListener('click', () => this.clearAll());

        this.form.addEventListener('change', (e) => {
          if (e.target.matches('.mf-price-input')) e.target.dataset.touched = 'true';
          this.syncActiveStates();
          this.apply();
        });

        this.form.addEventListener('input', (e) => {
          if (e.target.matches('.mf-price-input')) {
            e.target.dataset.touched = 'true';
            clearTimeout(this._priceTimer);
            this._priceTimer = setTimeout(() => this.apply(), 300);
          }
          if (e.target.matches('input[type="range"][data-filter-mode="max"]')) {
            e.target.dataset.touched = 'true';
            const output = e.target.closest('.mf-range-slider')?.querySelector('[data-range-output]');
            if (output) output.textContent = formatThousands(e.target.value);
            this.apply();
          }
        });
      }

      document.addEventListener('collection-mf-filters:refresh', this._onRefresh);
      this._onMerge = (e) => this.mergeFacetsFrom(e.detail && e.detail.doc);
      document.addEventListener('collection-mf-filters:merge', this._onMerge);
      this.syncMobileCollapse();
      this._onResize = () => this.syncMobileCollapse();
      window.addEventListener('resize', this._onResize);
      this.formatRangeOutputs();
      this.syncActiveStates();
      this.apply();
    }

    disconnectedCallback() {
      document.removeEventListener('collection-mf-filters:refresh', this._onRefresh);
      if (this._onMerge) document.removeEventListener('collection-mf-filters:merge', this._onMerge);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    formatRangeOutputs() {
      this.querySelectorAll('.mf-range-slider [data-range-output]').forEach((output) => {
        const raw = output.textContent.replace(/,/g, '').trim();
        if (raw !== '') output.textContent = formatThousands(raw);
      });
    }

    syncMobileCollapse() {
      const panel = this.querySelector('.collection-mf-filters__collapse');
      if (!panel) return;
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      if (isMobile) {
        if (!panel.dataset.mobileReady) {
          panel.open = false;
          panel.dataset.mobileReady = 'true';
        }
      } else {
        panel.open = true;
        delete panel.dataset.mobileReady;
      }
    }

    syncActiveStates() {
      this.querySelectorAll('.facet-checkbox').forEach((label) => {
        const input = label.querySelector('input[type="checkbox"]');
        const checked = Boolean(input?.checked);
        label.classList.toggle('active', checked);
        if (input) {
          if (checked) input.setAttribute('checked', '');
          else input.removeAttribute('checked');
        }
      });
    }

    getEmptyStateEl() {
      const layout = this.closest('.facets-vertical')
        || this.closest('.facets-horizontal')
        || this.closest('.shopify-section');
      return layout?.querySelector('[data-mf-filter-empty]');
    }

    getGrid() {
      const layout = this.closest('.facets-vertical')
        || this.closest('.facets-horizontal')
        || this.closest('.shopify-section');
      return layout?.querySelector('#product-grid') || document.getElementById('product-grid');
    }

    clearAll() {
      this.form?.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        cb.checked = false;
      });
      this.form?.querySelectorAll('.mf-price-input').forEach((inp) => {
        inp.value = '';
        delete inp.dataset.touched;
      });
      this.form?.querySelectorAll('input[type="range"][data-filter-mode="max"]').forEach((inp) => {
        inp.value = inp.max;
        inp.dataset.touched = 'false';
        const output = inp.closest('.mf-range-slider')?.querySelector('[data-range-output]');
        if (output) output.textContent = formatThousands(inp.value);
      });
      this.syncActiveStates();
      this.apply();
    }

    updateClearButton(filters) {
      const btn = this.querySelector('[data-mf-clear]');
      if (!btn) return;
      const hasRanges = Object.values(filters.ranges).some((a) => a.length);
      const hasList = Object.values(filters.lists).some((a) => a.length);
      const hasPrice = filters.priceMin != null || filters.priceMax != null;
      const hasMax = Object.values(filters.maxValues || {}).some((n) => n != null);
      btn.hidden = !(hasRanges || hasList || hasPrice || hasMax);
    }

    getSelectedFilters() {
      const filters = { ranges: {}, lists: {}, maxValues: {}, priceMin: null, priceMax: null };

      this.form?.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        const key = cb.dataset.filterKey;
        if (!key) return;

        if (cb.dataset.rangeSet) {
          if (!filters.ranges[key]) filters.ranges[key] = [];
          filters.ranges[key].push({ id: cb.value, set: cb.dataset.rangeSet });
        } else {
          if (!filters.lists[key]) filters.lists[key] = [];
          filters.lists[key].push(cb.value);
        }
      });

      const minInp = this.form?.querySelector('[name="mf_price_min"]');
      const maxInp = this.form?.querySelector('[name="mf_price_max"]');

      if (minInp?.dataset.touched && minInp.value.trim()) {
        filters.priceMin = parseMoneyInput(minInp.value, this.currency);
      }
      if (maxInp?.dataset.touched && maxInp.value.trim()) {
        filters.priceMax = parseMoneyInput(maxInp.value, this.currency);
      }

      this.form?.querySelectorAll('input[type="range"][data-filter-mode="max"]').forEach((inp) => {
        const key = inp.dataset.filterKey;
        if (!key) return;
        const maxValue = parseNum(inp.max);
        const currentValue = parseNum(inp.value);
        if (Number.isNaN(maxValue) || Number.isNaN(currentValue)) return;
        if (currentValue < maxValue) {
          filters.maxValues[key] = currentValue;
        }
      });

      return filters;
    }

    matchesList(attrVal, selected) {
      if (!selected?.length) return true;
      if (!attrVal) return false;
      return selected.includes(attrVal);
    }

    apply() {
      const grid = this.getGrid();
      if (!grid) return;

      const filters = this.getSelectedFilters();
      const hasRanges = Object.values(filters.ranges).some((a) => a.length);
      const hasList = Object.values(filters.lists).some((a) => a.length);
      const hasPrice = filters.priceMin != null || filters.priceMax != null;
      const hasMax = Object.values(filters.maxValues || {}).some((n) => n != null);
      const filtering = hasRanges || hasList || hasPrice || hasMax;

      let visible = 0;
      let productTotal = 0;

      grid.querySelectorAll(':scope > .grid__item').forEach((item) => {
        const root = getFilterRoot(item);
        const isProduct = root.hasAttribute('data-mf-price');

        if (!isProduct) {
          item.hidden = false;
          item.classList.remove('mf-filter-hidden');
          return;
        }

        productTotal += 1;
        let show = true;

        if (filtering) {
          const { min: priceLo, max: priceHi } = getProductPrices(root, item);
          if (hasPrice && !matchesPrice(priceLo, priceHi, filters)) {
            show = false;
          }

          if (show && hasRanges) {
            for (const [key, selected] of Object.entries(filters.ranges)) {
              if (!hasFilterField(root, key)) {
                show = false;
                break;
              }
              const num = getFieldNum(root, item, key);
              if (!matchesRange(num, selected)) {
                show = false;
                break;
              }
            }
          }

          if (show && hasList) {
            for (const [key, selected] of Object.entries(filters.lists)) {
              if (!hasListField(root, key)) {
                show = false;
                break;
              }
              const val = root.getAttribute(`data-mf-${key}`);
              if (!this.matchesList(val, selected)) {
                show = false;
                break;
              }
            }
          }

          if (show && hasMax) {
            for (const [key, maxValue] of Object.entries(filters.maxValues)) {
              if (!hasFilterField(root, key)) {
                show = false;
                break;
              }
              const num = getFieldNum(root, item, key);
              if (Number.isNaN(num) || num > maxValue) {
                show = false;
                break;
              }
            }
          }
        }

        item.classList.toggle('mf-filter-hidden', !show);
        item.hidden = !show;
        if (show) visible += 1;
      });

      this.updateCount(visible, productTotal);
      this.updateClearButton(filters);
      this.syncActiveStates();

      const emptyEl = this.getEmptyStateEl();
      if (emptyEl) {
        const showEmpty = filtering && productTotal > 0 && visible === 0;
        emptyEl.hidden = !showEmpty;
        emptyEl.classList.toggle('hidden', !showEmpty);
      }
    }

    // Sameinar síugildi úr síðu sem var hlaðið inn (infinite scroll) við núverandi síur:
    // nýjar síur, ný gildi í listasíum, hærra hámark á þyngd og verði.
    mergeFacetsFrom(doc) {
      if (!doc || !this.form) return;
      const srcForm = doc.querySelector('#CollectionMfFiltersForm');
      if (!srcForm) return;
      const order = (this.dataset.mfOrder || '').split(',').map((d) => d.split(':')[0]).filter(Boolean);
      const keyOf = (d) => {
        const m = String(d.className).match(/filter-mf-(\S+)/);
        return m ? m[1] : '';
      };
      const rank = (k) => {
        const i = order.indexOf(k);
        return i === -1 ? 999 : i;
      };
      let changed = false;

      srcForm.querySelectorAll('details.mf-js-filter').forEach((srcDet) => {
        const key = keyOf(srcDet);
        if (!key) return;
        const cur = this.form.querySelector(`details.mf-js-filter.filter-mf-${CSS.escape(key)}`);

        if (!cur) {
          const existing = Array.from(this.form.querySelectorAll('details.mf-js-filter'));
          if (!existing.length) return;
          const clone = document.importNode(srcDet, true);
          clone.removeAttribute('open');
          const before = existing.find((d) => rank(keyOf(d)) > rank(key));
          if (before) before.insertAdjacentElement('beforebegin', clone);
          else existing[existing.length - 1].insertAdjacentElement('afterend', clone);
          changed = true;
          return;
        }

        const curUl = cur.querySelector('ul.mf-filter-values');
        const srcUl = srcDet.querySelector('ul.mf-filter-values');
        if (curUl && srcUl) {
          const have = new Set(Array.from(curUl.querySelectorAll('input')).map((i) => i.value));
          srcUl.querySelectorAll(':scope > .facets__item').forEach((li) => {
            const inp = li.querySelector('input');
            if (!inp || have.has(inp.value)) return;
            const clone = document.importNode(li, true);
            clone.classList.remove('show-more-item', 'hidden');
            const ci = clone.querySelector('input');
            if (ci) ci.checked = false;
            curUl.appendChild(clone);
            have.add(inp.value);
            changed = true;
          });
        }

        const curRange = cur.querySelector('input[type="range"][data-filter-mode="max"]');
        const srcRange = srcDet.querySelector('input[type="range"][data-filter-mode="max"]');
        if (curRange && srcRange) {
          const oldMax = parseNum(curRange.max);
          const newMax = parseNum(srcRange.max);
          if (!Number.isNaN(newMax) && (Number.isNaN(oldMax) || newMax > oldMax)) {
            const atMax = Number.isNaN(oldMax) || parseNum(curRange.value) >= oldMax;
            curRange.max = srcRange.max;
            if (atMax) {
              curRange.value = srcRange.max;
              const slider = curRange.closest('.mf-range-slider');
              const output = slider && slider.querySelector('[data-range-output]');
              if (output) output.textContent = formatThousands(curRange.value);
            }
            changed = true;
          }
        }

        const curMaxInp = cur.querySelector('[name="mf_price_max"]');
        const srcMaxInp = srcDet.querySelector('[name="mf_price_max"]');
        if (curMaxInp && srcMaxInp) {
          const a = parseNum(curMaxInp.dataset.priceHint);
          const b = parseNum(srcMaxInp.dataset.priceHint);
          if (!Number.isNaN(b) && (Number.isNaN(a) || b > a)) {
            ['data-price-hint', 'max', 'placeholder'].forEach((attr) => {
              if (srcMaxInp.hasAttribute(attr)) curMaxInp.setAttribute(attr, srcMaxInp.getAttribute(attr));
            });
            const curMin = cur.querySelector('[name="mf_price_min"]');
            const srcMin = srcDet.querySelector('[name="mf_price_min"]');
            if (curMin && srcMin && srcMin.hasAttribute('max')) curMin.setAttribute('max', srcMin.getAttribute('max'));
            const s1 = cur.querySelector('.facets__selected');
            const s2 = srcDet.querySelector('.facets__selected');
            if (s1 && s2) s1.textContent = s2.textContent;
            changed = true;
          }
        }
      });

      if (changed) {
        sortValueLists(this);
        this.formatRangeOutputs();
        this.syncActiveStates();
        this.apply();
      }
    }

    updateCount(visible, total) {
      const sentinel = this.closest('.shopify-section') && this.closest('.shopify-section').querySelector('.infinite-product-grid-sentinel');
      const fullTotal = sentinel ? parseInt(sentinel.dataset.totalProducts || '0', 10) : 0;
      if (fullTotal > total) {
        if (visible === total) visible = fullTotal;
        total = fullTotal;
      }
      const text = visible === total
        ? `${visible} vörur`
        : `${visible} af ${total} vörum`;

      ['ProductCountDesktop', 'ProductCount'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
      });
    }
  }

  if (!customElements.get('collection-metafield-filters')) {
    customElements.define('collection-metafield-filters', CollectionMetafieldFilters);
  }
})();
